import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  TGenerateLewkboardsRequest,
  TGetCategoriesResponse,
} from "../../../../b2c-api/src/routers/lewkboards";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LewkAPI } from "@/config/api/api-client";
import {
  NetworkError,
  ClientError,
  ClientStatusCodes,
} from "@/lib/utils/error-definitions";
import { register } from "module";
import LewkLoader from "@/components/c-loader";
import { CheckIcon } from "lucide-react";

interface propTypes {
  disableProgress: React.Dispatch<React.SetStateAction<boolean>>;
}

const FormItemStep: React.FC<propTypes> = ({ disableProgress }) => {
  // ---------------------------- HOOKS ---------------------------- //
  const { setValue } = useFormContext<TGenerateLewkboardsRequest>();

  // ---------------------------- STATES ---------------------------- //
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [itemTypes, setItemTypes] = useState<TGetCategoriesResponse>();
  const [loading, setLoading] = useState<boolean>(false);

  // ---------------------------- FUNCTIONS ---------------------------- //
  const handleSelection = (itemName: string) => {
    if (selectedItems.includes(itemName)) {
      setSelectedItems(selectedItems.filter((name) => name !== itemName));
    } else if (selectedItems.length < 4) {
      setSelectedItems([...selectedItems, itemName]);
    }
  };

  const getItemTypes = async () => {
    try {
      setLoading(true);
      const { data, error } = await LewkAPI.lewkboards["get-categories"].get();
      //@ts-ignore
      if (error) throw new NetworkError(error.value["error"], error.status);
      setItemTypes(data);
    } catch (error) {
      if (error instanceof NetworkError) {
        error.handle();
      } else {
        new ClientError(
          error as string,
          ClientStatusCodes.UNDOCUMENTED_ERROR
        ).handle();
      }
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------- USE EFFECT ---------------------------- //
  useEffect(() => {
    setValue("itemTypes", selectedItems);
    if (selectedItems.length === 4) {
      disableProgress(false);
    } else {
      disableProgress(true);
    }
    return () => disableProgress(true);
  }, [selectedItems]);

  useEffect(() => {
    getItemTypes();
  }, []);
  return !itemTypes ? (
    <div className=" flex flex-col items-center h-full justify-center ">
      <LewkLoader />
    </div>
  ) : (
    <div className="flex flex-col gap-8 h-full justify-center">
      <div className=" flex flex-col justify-center text-center gap-1">
        <Label className=" text-center" variant={"multistep"}>
          What items do you want to see?
        </Label>
        <p className=" text-xs text-lewk-darkgrey">
          Select 4 items you’d like to see in your outfit!
        </p>
      </div>
      <div className=" flex gap-2 flex-wrap">
        {itemTypes.map((item, index) => (
          <Button
            key={index}
            type="button"
            variant={
              selectedItems.includes(item.value) ? "selected" : "secondary"
            }
            onClick={() => handleSelection(item.value)}
            className={` flex relative items-center w-[calc(50%-8px)] h-10 `}
          >
            <p className="capitalize">{item.label}</p>
            <div
              className={`flex absolute items-center justify-center p-1 right-1 size-7 rounded-full `}
            >
              <CheckIcon
                className={`stroke-[3px] ${
                  selectedItems.includes(item.value)
                    ? "text-white "
                    : "text-transparent"
                }`}
              />
            </div>
          </Button>
        ))}
      </div>
    </div>
  );
};

export default FormItemStep;
